import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dateFormat from "dateformat";

import {
  AssessmentCard,
  SimulationCard,
} from "src/pages/coaching/manage/courses/edit/components/cards";
import AssessmentReportView from "src/pages/coaching/manage/courses/edit/components/reports/assessmentReport";

import {
  fetchAssessmentReport,
  fetchCourseLearners,
  fetchLearningRecordCourse,
} from "src/redux/actions/coaching/learners";
import { CourseContext, SimulationReportState } from "../context";

import { LearningRecordCourse } from "src/types/coaching/learners";
import {
  flattenContents,
} from "src/pages/coaching/manage/courses/edit/helper";

import _ from "lodash";
import cx from "classnames";
import styles from "./reports.module.scss";
import Tooltip from "src/components/tooltip";
import Text from "src/components/text";
import { Dropdown } from "react-bootstrap";
import ordinal from "ordinal";
import { SimulationLearnerReportView } from "./component/simulationLearnerReportView/indesx";
// import { SimulationLearnerReportView } from "./component/simulationLearnerReportView/indesx";

interface CourseReportProps {
  course: LearningRecordCourse;
}

interface AttemptsDropdownProps {
  attempts: any[];
  selectedAttempt: any;
  // eslint-disable-next-line no-unused-vars
  onSelectAttempt: (attempt: any) => void;
}

interface LeftPaneProps {
  contents: any[];
  selectedContentId: string | null;
  selectedAttempt: any;
  sessions: any[];
  isSidebarCollapsed: boolean;
  loading: boolean;
  onToggleSidebar: () => void;
  onViewReport: (content: any) => void;
  onViewSimulationReport: (content: any) => void;
  onSelectAttempt: (attempt: any) => void;
  attempts: any[];
}

const LeftPaneView: React.FC<LeftPaneProps> = ({
  contents,
  selectedContentId,
  isSidebarCollapsed,
  loading,
  selectedAttempt,
  sessions,
  attempts,
  onToggleSidebar,
  onViewReport,
  onViewSimulationReport,
  onSelectAttempt,
}) => {

  const onContentClicked = (content: any) => {
    if (content.entityType === "assessment") {
      onViewReport(content);
    } else if (content.entityType === "simulation") {
      onViewSimulationReport(content);
    }
  }

  const getContentScore = (content: any) => {
    const session = sessions.find(
      (item: any) => item.type === content.entityType && item.id === content.id,
    );
    const score =
      content.entityType === "simulation"
        ? _.get(session, "score")
        : _.get(session, "report.score");

    if (score === undefined || score === null) return null;

    const scoreValue = content.entityType === "simulation"
      ? Math.round(score * 2 * 10)
      : Math.round(score * 100);

    return  content.content ? `${scoreValue}%` : null;
  };

  return (
    <div
      className={cx(styles.left, {
        [styles.leftCollapsed]: isSidebarCollapsed,
      })}
    >
      {/* Header with back button */}

      <div className={styles.header}>
        <div className={styles.titleContainer}>
          {!isSidebarCollapsed && (
            <div className={styles.title}>scoring</div>
          )}
          <Tooltip content="Toggle sidebar" placement="right">
            <button className={cx(styles.toggleButton, { [styles.toggleButtonCollapsed]: isSidebarCollapsed })} onClick={onToggleSidebar}>
              <i className="paperflite-side-panel" />
            </button>
          </Tooltip>
        </div>

        {!isSidebarCollapsed && _.size(attempts) === 0 && loading && (
          <div className={styles.placeholder}>
            <div className={cx(styles.attemptsLoader, styles.block)} />
          </div>
        )}

        {!isSidebarCollapsed && _.size(attempts) > 0 && (
          <AttemptsDropdown
            attempts={attempts}
            selectedAttempt={selectedAttempt}
            onSelectAttempt={onSelectAttempt}
          />
        )}
      </div>

      {/* Content list */}
      {!isSidebarCollapsed && _.size(contents) === 0 && loading && (
        <ReportPlaceholder />
      )}

      {!isSidebarCollapsed && _.size(contents) > 0 && (
        <div className={styles.contents}>
          {contents.map((content: any, index: number) => {
            const type = content.entityType;
            const key = content.uuid || index;

            if (type === "lesson" || type === "podcast") return null;

            if (type === "assessment") {
              return (
                <div
                  key={key}
                  className={cx(styles.cardWrapper, {
                    [styles.selectedCard]: selectedContentId === content.id,
                  })}
                >
                  <AssessmentCard
                    assessment={content}
                    assessmentIndex={index + 1}
                    index={index}
                    active={false}
                    score={getContentScore(content)}
                    mode="view"
                    readonly
                    onClick={() => onContentClicked(content)}
                    className={styles.assessmentCard}
                  />
                </div>
              );
            }

            if (type === "simulation") {
              return (
                <div
                  key={key}
                  className={cx(styles.cardWrapper, {
                    [styles.selectedCard]: selectedContentId === content.id,
                  })}
                >
                  <SimulationCard
                    asset={content}
                    index={index + 1}
                    lessonIndex={index + 1}
                    active={false}
                    score={getContentScore(content)}
                    mode="view"
                    readonly
                    onClick={() => onContentClicked(content)}
                  />
                </div>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
};

const CourseReport: React.FC<CourseReportProps> = ({ course }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate()

  const { courseId, learningId } = useParams();
  const courseContext = useContext(CourseContext);

  const learnerUserId = useSelector((state: any) =>
    _.get(state, "users.me.id"),
  );

  const [showReport, setShowReport] = useState(false);
  const [simulationReport, setSimulationReport] = useState<SimulationReportState | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<any>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const defaultedAttemptIdRef = useRef<string | null>(null);

  const setIsSidebarCollapsed = courseContext.setIsSidebarCollapsed;
  const isSidebarCollapsed = courseContext.isSidebarCollapsed;
  const activeTab = courseContext.activeTab;
  const setActiveTab = courseContext.setActiveTab;
  const secondaryView = courseContext.secondaryView;
  const changeSecondaryView = courseContext.changeSecondaryView;
 
  const attemptLearners: any[] = useSelector((state: any) =>
    _.get(state.courses, "attemptLearners.items", []),
  );

  const attemptsSource = useMemo(() => {
    const courseAttempts = _.get(course, "attempts", []);

    if (_.size(courseAttempts) > 0) {
      return courseAttempts;
    }

    if (_.size(attemptLearners) > 0) {
      return attemptLearners;
    }

    return course?.id ? [course] : [];
  }, [course, attemptLearners]);

  const openSimulationReport = useCallback(({ attempt, session, content }: SimulationReportState) => {
    setSimulationReport({ attempt, session, content });
  }, []);

  const closeSimulationReport = useCallback(() => {
    setSimulationReport(null);
  }, []);

  const toggleSidebar = () => {
    if (simulationReport && activeTab === 1 && secondaryView !== "call-recording-minimized") {
      changeSecondaryView?.("call-recording-minimized");
      setActiveTab?.(0);
      setIsSidebarCollapsed?.(false);
      return;
    }
    setIsSidebarCollapsed?.(!isSidebarCollapsed);
  };

  const attempts = useMemo(() => {
    return attemptsSource.map((item: any, index: number, arr: any[]) => ({
      ...item,
      attemptNumber: arr.length - index,
    }));
  }, [attemptsSource]);

  useEffect(() => {
    if (!attempts.length) return;
    const latestAttempt =
      attempts.find((a: any) => a.id === learningId) || attempts[0];
    setSelectedAttempt((current: any) => {
      return current?.id === latestAttempt.id ? current : latestAttempt;
    });
  }, [attempts, learningId]);

  const activeId = selectedAttempt?.id ?? learningId;

  const activeContent = useSelector((state: any) =>
    _.get(state.learners, `course[${activeId}].item`),
  );

  const activeContentLoading = useSelector((state: any) =>
    _.get(state.learners, `course[${activeId}].loading`, false),
  );

  const activeContentLoaded = useSelector((state: any) =>
    _.get(state.learners, `course[${activeId}].loaded`, false),
  );

  const activeCourseRecord = activeContent;

  const sessions = useMemo(
    () => _.get(activeCourseRecord, "sessions", []),
    [activeCourseRecord],
  );

  const sessionsKey = useMemo(() => {
    return sessions.map((session: any) => `${session.type}:${session.id}`).join("|");
  }, [sessions]);

  const selectedSession = useMemo(
    () => sessions.find((s: any) => s.id === selectedSessionId),
    [sessions, selectedSessionId],
  );

  const isReportLoaded = !!selectedSession?.report;

  const contents = useMemo(() => {
    return flattenContents(course?.course?.contents || []);
  }, [course?.course?.contents]);


  const scoreableContents = useMemo(() => {
    return contents.filter(
      (item: any) => item.entityType === "assessment" || item.entityType === "simulation",
    );
  }, [contents]);

  const leftPaneLoading = activeContentLoading && _.size(scoreableContents) === 0;

  const assessment = useMemo(() => {
    if (!selectedSession) return null;
    return contents.find(
      (content: any) =>
        content.entityType === "assessment" &&
        (content.id === selectedSession.id ||
          content.session === selectedSession.session),
    );
  }, [selectedSession, contents]);


  const lessonName = useMemo(
    () => _.get(assessment, "name", ""),
    [assessment],
  );

  const name = useMemo(() => _.get(course, "course.name", ""), [course]);

  const getAssessmentForContent = (content: any): any | null => {
    if (content.entityType === "assessment") return content;
    if (content.entityType === "section") {
      return contents.find(
        (f: any) =>
          f.parent === content.uuid && f.entityType === "assessment",
      );
    }
    return null;
  };

  const handleViewReport = (content: any) => {
    const assessmentItem = getAssessmentForContent(content);
    if (!assessmentItem) {
      return;
    }

    const session = sessions.find((s: any) => s.type === "assessment" && s.id === assessmentItem.id);

    if (!session) return;

    dispatch(
      fetchAssessmentReport({
        course: course.course!.id!,
        learningRecord: selectedAttempt?.id ?? learningId!,
        id: assessmentItem.id,
        type: assessmentItem.entityType,
        session: session?.session,
      }),
    );

    setSelectedSessionId(session.id);
    setSelectedContentId(assessmentItem.id);
    changeSecondaryView?.(null);
    closeSimulationReport();
    setShowReport(true);
  };

  const handleViewSimulationReport = (content: any) => {
    const session = sessions.find((s: any) => s.type === "simulation" && s.id === content.id);

    if (!session || !selectedAttempt) {
      return;
    }

    setShowReport(false);
    setSelectedContentId(content.id);
    changeSecondaryView?.(null);
    setActiveTab?.(0);
    openSimulationReport({
      attempt: selectedAttempt,
      session: session,
      content,
    });
  };

  const handleCloseReport = () => {
    setShowReport(false);
  };

  const onSelectAttempt = (attempt: any) => {
    defaultedAttemptIdRef.current = null;
    setSelectedAttempt(attempt);
    setSelectedContentId(null);
    setSelectedSessionId(null);
    setShowReport(false);
    changeSecondaryView?.(null);
    closeSimulationReport();
  };

  useEffect(() => {
    defaultedAttemptIdRef.current = null;
    setSelectedContentId(null);
    setSelectedSessionId(null);
    setShowReport(false);
    changeSecondaryView?.(null);
    closeSimulationReport();
  }, [activeId, changeSecondaryView, closeSimulationReport]);

  useEffect(() => {
    if (
      !selectedAttempt ||
      selectedContentId ||
      _.size(sessions) === 0 ||
      defaultedAttemptIdRef.current === selectedAttempt.id
    ) {
      return;
    }

    const firstContent = scoreableContents.find((content: any) => {
      return (
        sessions.some((session: any) => session.type === content.entityType && session.id === content.id)
      );
    });

    if (!firstContent) return;

    defaultedAttemptIdRef.current = selectedAttempt.id;

    if (firstContent.entityType === "assessment") {
      handleViewReport(firstContent);
    } else {
      handleViewSimulationReport(firstContent);
    }
  }, [selectedAttempt?.id, selectedContentId, sessionsKey, scoreableContents]);

  useEffect(() => {
    if (!activeId || activeContentLoading || activeContentLoaded) {return;}
    dispatch(fetchLearningRecordCourse(activeId));
  }, [activeId, activeContentLoading, activeContentLoaded]);

  useEffect(() => {
    if (!course?.course?.id || !activeId || _.size(sessions) === 0) return;
    
    sessions
      .filter((session: any) =>
        session.type === "assessment" &&
        session.session &&
        !session.fetchingReport &&
        !session.fetchedReport
      )
      .forEach((session: any) =>
        dispatch(
          fetchAssessmentReport({
            course: course.course!.id!,
            learningRecord: activeId,
            id: session.id,
            type: session.type,
            session: session.session,
          }),
        ),
      );
  }, [activeId, course?.course?.id, sessions]);

  useEffect(() => {
    if (courseId) {
      dispatch(
        fetchCourseLearners({
          course: courseId,
          status: "COMPLETED",
          page: 0,
          limit: 10,
        }),
      );
    }
  }, [courseId]);


  return (
    <div className={styles.container}>
      <div className={styles.goback}>
        <i
          className={cx(styles.back, "paperflite-left-arrow")}
          onClick={() => navigate(-1)}
        />
        <Text
          text={name}
          lines={1}
          className={cx(styles.title)}
          tooltip={{ placement: "bottom" }}
        />
      </div>
      <div
        className={cx(styles.wrapper, {
          [styles.sidebarCollapsed]: showReport || simulationReport,
        })}
      >
        <LeftPaneView
          contents={scoreableContents}
          selectedContentId={selectedContentId}
          isSidebarCollapsed={isSidebarCollapsed!}
          loading={leftPaneLoading}
          selectedAttempt={selectedAttempt}
          sessions={sessions}
          attempts={attempts}
          onToggleSidebar={toggleSidebar}
          onViewReport={handleViewReport}
          onViewSimulationReport={handleViewSimulationReport}
          onSelectAttempt={onSelectAttempt}
        />

        <div className={cx(styles.right,{[styles.rightExplanded]:(isSidebarCollapsed && activeTab === 1)},{[styles.rightExpand]:(isSidebarCollapsed && activeTab != 1)},{[styles.assessmentRight]:showReport})}>
          {simulationReport && (
            <div className={styles.simulationOverlayWrapper}>
              <div
                className={cx(styles.simulationOverlay, {
                  [styles.simulationOverlayVisible]: !!simulationReport,
                })}
              >
                <SimulationLearnerReportView
                  simulationReport={simulationReport}
                />
              </div>
            </div>
          )}

          {showReport && (
            <div className={styles.assessmentWrapper}>
              <div className={styles.lessonName}>{lessonName}</div>
              <AssessmentReportView
                className={styles.assessmentReport}
                course={course}
                assessment={assessment}
                report={selectedSession?.report}
                isReportLoaded={isReportLoaded}
                currentAttempt={selectedAttempt?.attemptNumber ?? 1}
                onEndAssessment={handleCloseReport}
                learnersView={true}
              />
            </div>
          )}
    </div>
      </div>
    </div>
  );
};

const ReportPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.cardContainer, styles.placeholder)}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={styles.card}>
          <div className={cx(styles.thumbnail, styles.block)} />
          <div className={styles.content}>
            <div className={cx(styles.titleLine, styles.block)} />
            <div className={cx(styles.metaLine, styles.block)} />
          </div>
        </div>
      ))}
    </div>
  );
};

const AttemptsDropdown: React.FC<AttemptsDropdownProps> = ({
  attempts,
  selectedAttempt,
  onSelectAttempt,
}) => {
  if (!attempts?.length) return null;

  const attempt = selectedAttempt || attempts[0];

  if (attempts.length === 1) {
    return (
      <div className={styles.attemtsFilterContainer}>
        <div className={styles.singleAttemptScore}>
          <span className={styles.score}>
            {Math.round((attempt?.score || 0) * 100)}%
          </span>
          <div className={styles.verticalDivider} />
          <span className={styles.attemptText}>
            {ordinal(attempt?.attemptNumber || 1)} attempt
          </span>
          {attempt?.modifiedDate && (
            <>
              <div className={styles.verticalDivider} />
              <span className={styles.date}>
                {dateFormat(
                  new Date(attempt.modifiedDate),
                  "dS mmm yyyy, HH:MM:ss",
                )}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.attemtsFilterContainer}>
      <Dropdown align="end">
        <Dropdown.Toggle as="button" className={styles.attemptDropdown}>
          <span className={styles.score}>
            {Math.round((selectedAttempt?.score || 0) * 100)}%
          </span>
          <div className={styles.verticalDivider} />
          <span className={styles.attemptText}>
            {ordinal(selectedAttempt?.attemptNumber || 1)} attempt
          </span>
          <div className={styles.verticalDivider} />
          <span className={styles.date}>
            {selectedAttempt?.modifiedDate
              ? dateFormat(
                  new Date(selectedAttempt.modifiedDate),
                  "dS mmm yyyy, HH:MM:ss",
                )
              : "NA"}
          </span>
          <i className={cx(styles.filterIcon, "paperflite-filter")} />
        </Dropdown.Toggle>

        <Dropdown.Menu className={styles.dropdownMenu}>
          {attempts.map((attempt: any) => {
            const active = attempt.id === selectedAttempt?.id;
            return (
              <Dropdown.Item
                key={attempt.id}
                className={cx(styles.dropdownItem, {
                  [styles.active]: active,
                })}
                onClick={() => onSelectAttempt(attempt)}
              >
                <div className={styles.attemps}>
                  <span className={styles.itemScore}>
                    {Math.round((attempt.score || 0) * 100)}%
                  </span>
                  <span className={styles.itemAttempt}>
                    {ordinal(attempt.attemptNumber)} attempt
                  </span>
                  <span className={styles.itemDate}>
                    {dateFormat(
                      new Date(attempt.modifiedDate),
                      "dS mmm yyyy, HH:MM:ss",
                    )}
                  </span>
                </div>
                {active && <i className="paperflite-tick-selected" />}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CourseReport;